import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import { DEFAULT_ACCOUNT_PREFERENCES } from "@shared/types/account-preferences";
import { getBootstrapPromise } from "@/api/bootstrap-api";
import { saveAccountPreferencesMap } from "@/api/command-api";
import { Logger } from "@/lib/logger";
import { INITIAL_DRAG_STATE, INITIAL_STACK_UNFURL_STATE, initialAppState, initialCardUIState, } from "./app-state";
import { loadDebugState, saveDebugState } from "./debug-persistence";
/**
 * PouchApp manages the UI state for the Pouch viewer.
 * Uses Zustand internally; children subscribe via `app.useStore((s) => ...)`.
 *
 * Account preferences are owned by this store — the sole source of truth.
 * The store is populated by `hydratePreferencesFromApi()` on login and
 * mutated optimistically by `setPreferences()`, which also pushes to the API.
 */
export class PouchApp {
    /**
     * Zustand store hook for reactive access in components.
     * Usage: `const cards = app.useStore((s) => s.cards)`
     */
    useStore;
    /**
     * Internal store - do not access directly, use methods or useStore.
     */
    store;
    /**
     * Counter for z-index assignment.
     */
    zIndexCounter = 1;
    /**
     * When false, `setPreferences()` updates the local store but does not
     * push to the server. Flipped on/off from `ApiSetup` based on auth.
     */
    apiSyncEnabled = false;
    toggleStackUnfurlController;
    registerStackUnfurlItemRefController;
    constructor() {
        const persistedDebug = loadDebugState();
        this.store = createStore(() => ({
            ...initialAppState,
            debug: { ...initialAppState.debug, ...persistedDebug },
            preferences: DEFAULT_ACCOUNT_PREFERENCES,
        }));
        this.useStore = (selector) => useStore(this.store, selector);
    }
    // ---------------------------------------------------------------------------
    // Properties (non-reactive snapshots)
    // ---------------------------------------------------------------------------
    get state() {
        return this.store.getState();
    }
    get cards() {
        return this.state.cards;
    }
    get selection() {
        return this.state.selection;
    }
    get ui() {
        return this.state.ui;
    }
    /**
     * Non-reactive snapshot of the active drag lifecycle. Useful for
     * handlers that need the freshest value without subscribing —
     * dnd-kit's `onDragMove` reads `app.drag.activeBlockId` to gate the
     * drag-rescue path, for example. UI consumers should prefer
     * `app.useStore((s) => s.drag.…)` selectors so they only re-render
     * when their slice actually changes.
     */
    get drag() {
        return this.state.drag;
    }
    get stackUnfurl() {
        return this.state.stackUnfurl;
    }
    // ---------------------------------------------------------------------------
    // Internal State Updates
    // ---------------------------------------------------------------------------
    setState(partial) {
        this.store.setState((s) => ({ ...s, ...partial }));
        return this;
    }
    setUI(partial) {
        return this.setState({ ui: { ...this.state.ui, ...partial } });
    }
    // ---------------------------------------------------------------------------
    // Card State
    // ---------------------------------------------------------------------------
    /**
     * Get card state, creating default if not exists.
     */
    getCardState(cardId) {
        return this.state.cards.get(cardId) ?? initialCardUIState;
    }
    /**
     * Set partial card state. Creates card if doesn't exist.
     */
    setCardState(cardId, partial) {
        const current = this.getCardState(cardId);
        const updated = new Map(this.state.cards);
        updated.set(cardId, { ...current, ...partial });
        return this.setState({ cards: updated });
    }
    // ---------------------------------------------------------------------------
    // Z-Index
    // ---------------------------------------------------------------------------
    /**
     * Bring a card to front by assigning highest z-index.
     */
    bringToFront(cardId) {
        return this.setCardState(cardId, { zIndex: this.zIndexCounter++ });
    }
    // ---------------------------------------------------------------------------
    // Selection
    // ---------------------------------------------------------------------------
    /**
     * Toggle card selection. Also moves the shift-click anchor to
     * `cardId` so the next shift-click measures from this interaction,
     * matching macOS Finder semantics (cmd-click both selects/deselects
     * AND re-anchors).
     *
     * Auto-exits multiselect mode when the toggle leaves the selection
     * empty — a 0-block selection has nothing to act on, so keeping the
     * action bar mounted would just trap the user behind a useless
     * affordance. Mirrors the "click outside while only one is selected"
     * exit path in `<Stream>`.
     */
    toggleSelection(cardId) {
        const next = new Set(this.state.selection);
        if (next.has(cardId)) {
            next.delete(cardId);
        }
        else {
            next.add(cardId);
        }
        if (next.size === 0 && this.state.ui.isMultiselectMode) {
            return this.setState({
                selection: next,
                selectionAnchor: undefined,
            }).setUI({ isMultiselectMode: false });
        }
        return this.setState({ selection: next, selectionAnchor: cardId });
    }
    /**
     * Clear all selections. Also exits multiselect mode and drops the
     * shift-click anchor — once nothing is selected there is no useful
     * reason to stay in the mode, and the next entry into multiselect
     * should start with a fresh anchor.
     */
    clearSelection() {
        return this.setState({ selection: new Set(), selectionAnchor: undefined }).setUI({
            isMultiselectMode: false,
        });
    }
    /**
     * Select a card. Re-anchors shift-click to `cardId` for the same
     * reason as `toggleSelection`.
     */
    selectCard(cardId) {
        const next = new Set(this.state.selection);
        next.add(cardId);
        return this.setState({ selection: next, selectionAnchor: cardId });
    }
    /**
     * Shift-click range selection. `orderedBlockIds` is the
     * DOM-document order of all blocks visible in the surface the user
     * clicked in (the caller computes this by walking
     * `[data-block-id]` within the closest `.stream` ancestor of the
     * click target) — i.e. the visual reading order, which is what
     * Finder-style range selection measures against.
     *
     * Behavior:
     *   - No anchor yet (first shift-click, or anchor is offscreen/
     *     gone) → fall back to the same path as `enterMultiselectMode`:
     *     enter multiselect with just `cardId` selected, and seed the
     *     anchor.
     *   - Anchor present and in the ordered list → add every id
     *     between anchor and `cardId` (inclusive, in either direction)
     *     to `selection`, and move the anchor to `cardId` so a
     *     follow-up shift-click extends from the new endpoint.
     *
     * Always enters / stays in multiselect mode and clears any active
     * collection / stack multi-selects (mutually exclusive modes).
     */
    selectRange(cardId, orderedBlockIds) {
        const anchor = this.state.selectionAnchor;
        const targetIdx = orderedBlockIds.indexOf(cardId);
        const anchorIdx = anchor !== undefined ? orderedBlockIds.indexOf(anchor) : -1;
        if (anchor === undefined || anchorIdx === -1 || targetIdx === -1) {
            const next = new Set(this.state.selection);
            next.add(cardId);
            return this.setState({
                selection: next,
                collectionSelection: new Set(),
                stackSelection: new Set(),
                selectionAnchor: cardId,
            }).setUI({
                isMultiselectMode: true,
                activeBlockId: undefined,
                isFocusMode: false,
            });
        }
        const [lo, hi] = anchorIdx < targetIdx ? [anchorIdx, targetIdx] : [targetIdx, anchorIdx];
        const next = new Set(this.state.selection);
        for (let i = lo; i <= hi; i++) {
            const id = orderedBlockIds[i];
            if (id !== undefined)
                next.add(id);
        }
        return this.setState({
            selection: next,
            collectionSelection: new Set(),
            stackSelection: new Set(),
            selectionAnchor: cardId,
        }).setUI({
            isMultiselectMode: true,
            activeBlockId: undefined,
            isFocusMode: false,
        });
    }
    // ---------------------------------------------------------------------------
    // UI State
    // ---------------------------------------------------------------------------
    /**
     * Set the active block shown in the slate sidebar. Resets focus mode so a
     * freshly-opened block always lands in the split view.
     */
    setActiveBlock(blockId) {
        return this.setUI({ activeBlockId: blockId, isFocusMode: false });
    }
    /**
     * Clear the active block (close the slate sidebar).
     */
    clearActiveBlock() {
        return this.setUI({ activeBlockId: undefined, isFocusMode: false });
    }
    setFocusMode(on) {
        return this.setUI({ isFocusMode: on });
    }
    toggleFocusMode() {
        return this.setUI({ isFocusMode: !this.state.ui.isFocusMode });
    }
    /**
     * Set the stream/slate content-area opacity. `1` is fully visible;
     * lower values dim the content so chrome (e.g. the omnibar draft input)
     * can take focus. Clamped to [0, 1].
     */
    setStreamFadeOpacity(value) {
        const clamped = Math.max(0, Math.min(1, value));
        return this.setUI({ streamFadeOpacity: clamped });
    }
    /**
     * Publish the omnibar's live draft text so other surfaces can react —
     * notably the host's filter strip, which renders a 5-col palette of
     * matching Collections while the bar is in Ask mode and the query is
     * non-empty. `DraftBar` is the sole writer; pass `""` to clear (e.g.
     * on submit). No-ops when the value matches the current state so
     * identical-keystroke commits don't churn subscribers.
     */
    setOmnibarQuery(value) {
        if (this.state.ui.omnibarQuery === value)
            return this;
        return this.setUI({ omnibarQuery: value });
    }
    /**
     * Set the active omnibar mode (`add` ↔ `ask`). `DraftBar` is the sole
     * writer — Tab inside the textarea, or the switch-style toggle in the
     * pill's mode cluster, both call this. Lifted out of `DraftBar`'s
     * local state so the host can gate Ask-only chrome (the filter strip,
     * the extended rise gradient) on the current value without prop
     * drilling.
     */
    setOmnibarMode(mode) {
        if (this.state.ui.omnibarMode === mode)
            return this;
        return this.setUI({ omnibarMode: mode });
    }
    /**
     * Publish the currently-unfurled stack id. `useStackUnfurl` is the sole
     * caller — it mirrors its local `openStackId` here so the omnibar's
     * destination chip can target the open stack while it's expanded and
     * revert to the route-level collection once it closes. Pass
     * `undefined` to clear (stack closed).
     */
    setOpenStackId(id) {
        if (this.state.ui.openStackId === id)
            return this;
        return this.setUI({ openStackId: id });
    }
    /**
     * Update the inline stack-unfurl lifecycle. `showUnfurledItems` is
     * derived from `phase + envelopes` here so every subscriber sees a
     * coherent slice after a single store write.
     */
    setStackUnfurl(partial) {
        const base = { ...this.state.stackUnfurl, ...partial };
        const next = {
            ...base,
            showUnfurledItems: base.envelopes !== undefined &&
                (base.phase === "opening" || base.phase === "open" || base.phase === "closing"),
        };
        const prev = this.state.stackUnfurl;
        if (prev.openStackId === next.openStackId &&
            prev.phase === next.phase &&
            prev.envelopes === next.envelopes &&
            prev.showUnfurledItems === next.showUnfurledItems) {
            return this;
        }
        return this.setState({ stackUnfurl: next });
    }
    resetStackUnfurl() {
        if (this.state.stackUnfurl === INITIAL_STACK_UNFURL_STATE)
            return this;
        return this.setState({ stackUnfurl: INITIAL_STACK_UNFURL_STATE });
    }
    setStackUnfurlController(toggleStack, registerItemRef) {
        this.toggleStackUnfurlController = toggleStack;
        this.registerStackUnfurlItemRefController = registerItemRef;
        return this;
    }
    clearStackUnfurlController(toggleStack) {
        if (this.toggleStackUnfurlController === toggleStack) {
            this.toggleStackUnfurlController = undefined;
            this.registerStackUnfurlItemRefController = undefined;
        }
        return this;
    }
    toggleStackUnfurl(stack) {
        this.toggleStackUnfurlController?.(stack);
        return this;
    }
    registerStackUnfurlItemRef(blockId, el) {
        this.registerStackUnfurlItemRefController?.(blockId, el);
        return this;
    }
    /**
     * Publish a single-shot request to auto-open + auto-enter inline
     * title-rename for a freshly-created Stack. Called by the
     * "Create empty stack" affordance in the collection header AFTER
     * `collectionStore.createStack` has settled — the server-side row
     * exists by then, so when `<StackBlockView>` consumes the signal
     * and routes through `useStackUnfurl().toggleStack`, the unfurl's
     * children query won't race against an in-flight create.
     *
     * The matching `<StackBlockView>` is responsible for clearing the
     * signal as part of consuming it; see `clearPendingNewStack`.
     */
    requestNewStackOpenAndEdit(stackId) {
        return this.setUI({ pendingNewStackId: stackId });
    }
    /**
     * Clear the pending-new-stack signal. Called by `<StackBlockView>`
     * once it has acted on a matching `pendingNewStackId` (opened the
     * unfurl + flipped `isEditing` to true) so subsequent re-renders
     * don't keep firing the same effect.
     */
    clearPendingNewStack() {
        if (this.state.ui.pendingNewStackId === undefined)
            return this;
        return this.setUI({ pendingNewStackId: undefined });
    }
    /**
     * Publish whether the omnibar's input surface currently holds focus.
     * `DraftBar` is the sole writer (focus / blur on the active textarea /
     * editor, the pointerdown-outside dismiss handler, the minimize
     * handler). Cross-subtree consumers — notably `MainSlate`'s stack-
     * isolation dim — read this directly rather than via prop drilling
     * through the slate / host hierarchy.
     */
    setOmnibarFocused(focused) {
        if (this.state.ui.isOmnibarFocused === focused)
            return this;
        return this.setUI({ isOmnibarFocused: focused });
    }
    // ---------------------------------------------------------------------------
    // Multiselect mode
    //
    // A distinct UI mode (long-press on a block to enter) that turns tap-to-open
    // into tap-to-select across the whole stream, and shows a persistent action
    // bar at the bottom for bulk operations (add to collection, …).
    // ---------------------------------------------------------------------------
    /**
     * Enter block multiselect mode and seed the selection with `cardId`.
     * Also closes the detail sidebar and clears any active
     * collection- / stack-multiselect — the three modes are mutually
     * exclusive.
     */
    enterMultiselectMode(cardId) {
        const selection = new Set(this.state.selection);
        selection.add(cardId);
        return this.setState({
            selection,
            collectionSelection: new Set(),
            stackSelection: new Set(),
            selectionAnchor: cardId,
        }).setUI({
            isMultiselectMode: true,
            activeBlockId: undefined,
            isFocusMode: false,
        });
    }
    /**
     * Exit multiselect mode and clear block, collection, AND stack
     * selections. Single switch covers all three kinds since they share
     * the `isMultiselectMode` flag. Also drops the shift-click anchor.
     */
    exitMultiselectMode() {
        return this.setState({
            selection: new Set(),
            collectionSelection: new Set(),
            stackSelection: new Set(),
            selectionAnchor: undefined,
        }).setUI({ isMultiselectMode: false });
    }
    /**
     * Enter collection multiselect mode and seed it with `collectionId`.
     * Mirrors `enterMultiselectMode` but operates on the parallel
     * `collectionSelection` set; clears block + stack selections so the
     * action bar shows collection-only actions.
     */
    enterCollectionMultiselectMode(collectionId) {
        const collectionSelection = new Set(this.state.collectionSelection);
        collectionSelection.add(collectionId);
        return this.setState({
            selection: new Set(),
            collectionSelection,
            stackSelection: new Set(),
        }).setUI({
            isMultiselectMode: true,
            activeBlockId: undefined,
            isFocusMode: false,
        });
    }
    /**
     * Toggle a collection's membership in the collection-multiselect
     * set. Auto-exits multiselect mode when the toggle leaves the set
     * empty, matching `toggleSelection`'s block-side convention — a
     * 0-collection selection has nothing to act on, so the mode should
     * tear itself down rather than hover empty.
     */
    toggleCollectionSelection(collectionId) {
        const next = new Set(this.state.collectionSelection);
        if (next.has(collectionId)) {
            next.delete(collectionId);
        }
        else {
            next.add(collectionId);
        }
        if (next.size === 0 && this.state.ui.isMultiselectMode) {
            return this.setState({ collectionSelection: next }).setUI({
                isMultiselectMode: false,
            });
        }
        return this.setState({ collectionSelection: next });
    }
    /**
     * Enter stack multiselect mode and seed it with `stackId`. Mirrors
     * `enterCollectionMultiselectMode` but operates on the parallel
     * `stackSelection` set so the action bar can offer Stack-specific
     * actions (Unstack) without conflating with layer-1 Collection
     * actions (Add to group). Clears block + collection selections.
     */
    enterStackMultiselectMode(stackId) {
        const stackSelection = new Set(this.state.stackSelection);
        stackSelection.add(stackId);
        return this.setState({
            selection: new Set(),
            collectionSelection: new Set(),
            stackSelection,
        }).setUI({
            isMultiselectMode: true,
            activeBlockId: undefined,
            isFocusMode: false,
        });
    }
    /**
     * Toggle a stack's membership in the stack-multiselect set.
     * Auto-exits multiselect mode when the toggle leaves the set empty
     * — same "empty selection ⇒ tear down" rule as the block + collection
     * variants.
     */
    toggleStackSelection(stackId) {
        const next = new Set(this.state.stackSelection);
        if (next.has(stackId)) {
            next.delete(stackId);
        }
        else {
            next.add(stackId);
        }
        if (next.size === 0 && this.state.ui.isMultiselectMode) {
            return this.setState({ stackSelection: next }).setUI({
                isMultiselectMode: false,
            });
        }
        return this.setState({ stackSelection: next });
    }
    /**
     * Publish the container the current selection lives in so the
     * multiselect action bar can offer context-aware actions (e.g.
     * "Create stack" only when inside a layer-1 Collection).
     *
     * Pass `undefined` to clear — typically called from a screen's
     * unmount cleanup when leaving a collection-backed surface.
     */
    setSelectionContainer(container) {
        return this.setUI({ selectionContainer: container });
    }
    /**
     * Clear sidebar, selection, lasso, and card UI state after a block is deleted.
     */
    forgetDeletedBlock(blockId) {
        const needClearActiveBlock = this.state.ui.activeBlockId === blockId;
        const ui = needClearActiveBlock
            ? { ...this.state.ui, activeBlockId: undefined, isFocusMode: false }
            : this.state.ui;
        const selection = new Set(this.state.selection);
        selection.delete(blockId);
        const lassoSelection = new Set(this.state.lassoSelection);
        lassoSelection.delete(blockId);
        const cards = new Map(this.state.cards);
        cards.delete(blockId);
        const selectionAnchor = this.state.selectionAnchor === blockId ? undefined : this.state.selectionAnchor;
        return this.setState({ ui, selection, lassoSelection, cards, selectionAnchor });
    }
    // ---------------------------------------------------------------------------
    // Lasso (Multi-Select)
    // ---------------------------------------------------------------------------
    startLasso() {
        return this.setState({ lassoSelection: new Set() }).setUI({ isLassoActive: true });
    }
    setLassoSelection(cardIds) {
        return this.setState({ lassoSelection: cardIds });
    }
    endLasso() {
        const lassoed = this.state.lassoSelection;
        const merged = new Set(this.state.selection);
        for (const id of lassoed)
            merged.add(id);
        return this.setState({ selection: merged, lassoSelection: new Set() }).setUI({
            isLassoActive: false,
        });
    }
    cancelLasso() {
        return this.setState({ lassoSelection: new Set() }).setUI({ isLassoActive: false });
    }
    // ---------------------------------------------------------------------------
    // Debug
    // ---------------------------------------------------------------------------
    setDebug(partial) {
        const next = { ...this.state.debug, ...partial };
        this.setState({ debug: next });
        saveDebugState(next);
        return this;
    }
    toggleWipBorder() {
        return this.setDebug({ showWipBorder: !this.state.debug.showWipBorder });
    }
    // ---------------------------------------------------------------------------
    // Preferences — single source of truth lives in the store
    // ---------------------------------------------------------------------------
    setApiSyncEnabled(enabled) {
        this.apiSyncEnabled = enabled;
        return this;
    }
    /**
     * Hydrate preferences from the server on login. Joins the shared
     * `/v1/bootstrap` promise so we don't fire a second round-trip
     * just for preferences — every cold-load consumer awaits the same
     * fetch. See `api/bootstrap-api.ts`.
     */
    async hydratePreferencesFromApi() {
        if (this.apiSyncEnabled === false)
            return;
        try {
            const { preferences } = await getBootstrapPromise();
            this.setState({ preferences: preferences });
        }
        catch (e) {
            Logger.warn("Failed to fetch preferences during bootstrap", {
                error: e instanceof Error ? e.message : String(e),
            });
        }
    }
    /** Reset preferences to defaults. Call on logout. */
    clearPreferences() {
        return this.setState({ preferences: DEFAULT_ACCOUNT_PREFERENCES });
    }
    setPreferences(patch) {
        const merged = { ...this.state.preferences, ...patch };
        this.setState({ preferences: merged });
        if (this.apiSyncEnabled === false)
            return this;
        void saveAccountPreferencesMap(patch)
            .then((server) => {
            this.setState({ preferences: server });
        })
            .catch((e) => {
            Logger.warn("Failed to push preferences to API", {
                error: e instanceof Error ? e.message : String(e),
            });
        });
        return this;
    }
    getPreference(key) {
        return this.state.preferences[key];
    }
    // ---------------------------------------------------------------------------
    // Drag lifecycle
    //
    // Centralized drag state that replaces the per-`<Stream>` useState +
    // Context.Provider trio (`ReorderDragContext`, `LiveDragTransformContext`,
    // `MultiDragContext`). Why move it here:
    //
    //   - `<Stream>`-level useState forces `<Stream>` itself to re-render
    //     on every drag move, which in turn re-runs the ~2300-line jsx()
    //     body and creates new Provider value objects, which cascade
    //     re-renders to every `<SortableBlockViewHost>` consumer regardless
    //     of memoization.
    //   - Zustand's fine-grained selectors (`app.useStore((s) => …)`)
    //     short-circuit re-renders via Object.is — a non-dragged block
    //     subscribing to `s.drag.activeBlockId === myId` returns `false`
    //     on every move and never re-renders.
    //
    // The dnd-kit `DndContext` itself stays mounted by `<Stream>` (sensors,
    // collision detection, droppable registry); only the lifecycle STATE
    // moves to the central store.
    // ---------------------------------------------------------------------------
    /**
     * Open a fresh drag lifecycle. Called from dnd-kit's `onDragStart` —
     * populates the leader id, the source scope key, the stack-create gate
     * (copied from `<Stream>`'s `createStackOnDropParentId` prop so any
     * consumer can read the gate without prop-drilling), and the
     * multi-drag deck info if the drag began on a multiselected block.
     *
     * `liveTransform` is seeded to `(0, 0)` rather than `undefined` so the
     * actively-dragged host paints at rest at pickup — the first
     * `onDragMove` overwrites it the moment the pointer moves.
     *
     * Idempotent against double-fire: if a drag is somehow already in
     * flight, the new payload simply overwrites — no half-cleared state.
     */
    setDragStart(opts) {
        const next = {
            activeBlockId: opts.activeBlockId,
            activeSourceScopeKey: opts.sourceScopeKey,
            foreignPointerScopeKey: undefined,
            stackOnDropParentId: opts.stackOnDropParentId,
            liveTransform: { x: 0, y: 0 },
            multi: opts.multi,
        };
        return this.setState({ drag: next });
    }
    /**
     * Update per-frame drag fields. Called from dnd-kit's `onDragMove` —
     * writes the live `event.delta` into `liveTransform` (a NEW object
     * each call, so the active block's selector trips and re-renders)
     * and updates `foreignPointerScopeKey` from the caller's pointer
     * hit-test.
     *
     * No-ops when no drag is in flight (defensive — `onDragMove` can fire
     * after a `setDragEnd` in pathological race conditions; we don't want
     * to resurrect a finished drag).
     *
     * Note: `foreignPointerScopeKey` is a primitive string|undefined, so
     * Object.is in subscribers handles equality automatically — no
     * special pre-check needed before writing. The new outer `drag`
     * object reference will fail Object.is for whole-slice selectors,
     * but field-level selectors (`s.drag.foreignPointerScopeKey`) see the
     * stable primitive and skip the re-render.
     */
    setDragMove(opts) {
        const prev = this.state.drag;
        if (prev.activeBlockId === undefined)
            return this;
        const next = {
            ...prev,
            liveTransform: opts.liveTransform,
            foreignPointerScopeKey: opts.foreignPointerScopeKey,
        };
        return this.setState({ drag: next });
    }
    /**
     * Close the drag lifecycle. Called from dnd-kit's `onDragEnd` AND
     * `onDragCancel` (the same reset works for both — a cancel and a
     * release both leave the slice fully cleared).
     *
     * Resets to `INITIAL_DRAG_STATE` rather than building a fresh object
     * each call so consumers comparing against the sentinel can early-out
     * cheaply, and so cleared state is structurally identical to the
     * "never-dragged" initial state (no lingering `liveTransform: {0,0}`
     * to confuse selectors).
     */
    setDragEnd() {
        if (this.state.drag === INITIAL_DRAG_STATE)
            return this;
        return this.setState({ drag: INITIAL_DRAG_STATE });
    }
}
